import { useToast } from 'vue-toastification'

import { useEnhancedFlyout } from '@/composables/useEnhancedFlyout'
import { useRewardCrossingGuard } from '@/composables/useRewardCrossingGuard'
import {
  cartChangeErrorMessage,
  cartDependencyErrorMessage,
  selectionSwapErrorMessage
} from '@/lib/cartErrors'

export interface CartMutationFeedbackOptions {
  /** The cart mutation to run (add / remove / package-builder submit). */
  mutate: () => Promise<unknown>
  /**
   * Side effects to run after the mutation succeeds, before the success toast — e.g. GTM tracking or
   * follow-on navigation. Runs inside the same try, so a throw here surfaces the error toast.
   */
  onSuccess?: () => void | Promise<void>
  /**
   * Generic success toast copy. Auto-suppressed where the enhanced flyout is active (it opens and
   * animates instead), and — on excluded routes — when the reward toast already covers this mutation.
   */
  successMessage: string
  /** Error toast copy shown if `mutate` (or `onSuccess`) throws; the error is re-thrown. */
  errorMessage: string
}

export interface CartMutationFeedback {
  run: (options: CartMutationFeedbackOptions) => Promise<void>
}

/**
 * Wraps the shared ceremony around a cart mutation: capture the pre-mutation subtotal, run it, fire
 * any success side effects, then show the generic add/remove toast unless something else covers it, and
 * surface an error toast on failure. Centralizes the pattern that was duplicated across
 * `bundleListItem`, `QuickAddsFlyout`, `ConfigureView`, and `PackageBuilderFooter`.
 *
 * **Two independent reasons the success toast is skipped, and both are needed** (Gap 11):
 *
 * 1. **The enhanced flyout is active.** It opens and animates, which *is* the feedback — a toast on top
 *    would be noise competing with the sequence. Suppression is coupled to the gate rather than global:
 *    excluded routes keep today's behaviour wholesale, because there the toast is the only signal a
 *    mutation happened at all.
 * 2. **A reward toast already covers this mutation** (`useRewardCrossingGuard`, BugHerd #108). Still
 *    needed *because* of the coupling above: on excluded routes both toasts still fire, so the
 *    collision it prevents is still reachable.
 *
 * The error toast is never gated. A failed mutation needs to say so on every route, enhanced or not —
 * silence there is indistinguishable from success.
 */
export function useCartMutationFeedback(): CartMutationFeedback {
  const toast = useToast()
  const guard = useRewardCrossingGuard()
  const { isEnhancedFlyoutActive } = useEnhancedFlyout()

  return {
    run: async ({ mutate, onSuccess, successMessage, errorMessage }) => {
      const subtotalBefore = guard.captureSubtotal()
      try {
        await mutate()
        await onSuccess?.()

        const suppressed =
          isEnhancedFlyoutActive.value || guard.shouldSuppressGenericCartToast(subtotalBefore)

        if (!suppressed) {
          toast.success(successMessage)
        }
      } catch (err) {
        // Error-specific copy wins over the caller's generic line when the failure
        // has a more accurate story to tell — a busy cart, a server-side dependency
        // message, or a selection swap that rolled back (where "failed to add" would
        // be wrong, since the add succeeded before the swap was undone).
        toast.error(
          cartChangeErrorMessage(
            err,
            selectionSwapErrorMessage(err) ?? cartDependencyErrorMessage(err) ?? errorMessage
          )
        )
        throw err
      }
    }
  }
}
